import IconFeatureFlag from '@/assets/svg/feature-flag.svg?react';
import LanguageSelector from '@/components/LanguageSelector';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import useIsCompanyUser from '@/hooks/useIsCompanyUser';
import { RoutesList } from '@/routes/routesList';
import { logoutOAuth } from '@/services/auth/oauthService';
import { useCronofyBulkUpdateEventsPrivacy } from '@/services/cronofy/hooks';
import { useUpdateUserInfo, useUserInfo } from '@/services/user/hooks';
import useSettingsStore from '@/stores/useSettings';
import { IconChevron, IconLogout } from '@/theme/icons';
import rem from '@/utils/rem';
import { signOutTracking, trackFeedbackSelected } from '@/utils/tracking';
import {
  Box,
  Button,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Heading,
  IconButton,
  Link,
  Switch,
  Text,
  VStack,
  useToken,
} from '@chakra-ui/react';
import { ChangeEvent, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ApiKeysSettings from './ApiKeysSettings';
import CalendarsSettings from './CalendarsSettings';
import ConnectedAppsSettings from './ConnectedAppsSettings';

type SettingsSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
};

function SettingsSidebar({ isOpen, onClose }: SettingsSidebarProps) {
  const { t } = useTranslation('SettingsSidebar');
  const { data: userInfo } = useUserInfo();
  const { isEnabled: showRpmHelpCenterButton } = useFeatureFlag(FEATURE_FLAGS.SHOW_RPM_HELP_CENTER_BUTTON);
  const { isCompanyUser } = useIsCompanyUser();
  const navigate = useNavigate();

  const { showDeclinedEvents, toggleShowDeclinedEvents } = useSettingsStore();

  const handleShowDeclinedEventsToggle = useCallback(() => {
    toggleShowDeclinedEvents();
  }, [toggleShowDeclinedEvents]);

  const [strokePrimary, backgroundDefault] = useToken('colors', ['stroke-primary', 'background-default']);

  const separatorColor = `color-mix(in srgb, ${strokePrimary} 70%, transparent)`;

  const handleLogout = async () => {
    signOutTracking();
    await logoutOAuth();
  };

  const handleEmailWithFallback = (email: string, onBeforeEmail?: () => void) => {
    onBeforeEmail?.();

    const mailtoUrl = `mailto:${email}`;
    const fallbackUrl = 'https://www.tonyrobbins.com/help';

    const startTime = Date.now();
    let blurTriggered = false;

    const handleBlur = () => {
      blurTriggered = true;
      window.removeEventListener('blur', handleBlur);
    };

    window.addEventListener('blur', handleBlur);
    window.location.href = mailtoUrl;

    setTimeout(() => {
      window.removeEventListener('blur', handleBlur);

      if (!blurTriggered || Date.now() - startTime < 100) {
        window.open(fallbackUrl, '_blank');
      }
    }, 500);
  };

  const [chatbotWidth, setChatbotWidth] = useState(0);

  useLayoutEffect(() => {
    // When opening this sidebar, Safari prints this error:
    //   Blocked a frame with origin "https://local.rpmplanner.com:3000" from accessing a frame with origin "https://chat.rpmplanner.com". Protocols, domains, and ports must match.
    // The error is not caused by the code in this effect though, but by the focus-lock library used by Chakra UI.
    // I think Chakra tries to access the iframe to not make it focusable while the sidebar is open.
    // Not worth trying to fix the error, it doesn't break anything at all.
    const chatbot = document.getElementById('chat-iframe');
    if (chatbot) setChatbotWidth(chatbot.offsetWidth);
  }, []);

  const playSoundEffects = useMemo(() => userInfo?.me?.soundsEnabled, [userInfo?.me?.soundsEnabled]);
  const areActionsPrivate = useMemo(() => userInfo?.me?.areActionsPrivate, [userInfo?.me?.areActionsPrivate]);
  const updateUserInfo = useUpdateUserInfo();
  const cronofyBulkUpdateEventsPrivacy = useCronofyBulkUpdateEventsPrivacy();

  const handleSoundEffectsToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!userInfo?.me?.id) {
        return;
      }

      //!TODO: we should change this mutation so we don't need to also pass in toThrive
      updateUserInfo.mutate({
        id: userInfo.me.id,
        soundsEnabled: !!event.target.checked,
        toThrive: userInfo?.me?.toThrive ?? '',
        language: userInfo?.me.language ?? 'en',
        isAllowedFeatureFlag: userInfo?.me?.isAllowedFeatureFlag ?? false,
        areActionsPrivate: userInfo?.me?.areActionsPrivate ?? true,
      });
    },
    [updateUserInfo, userInfo?.me?.id, userInfo?.me],
  );

  const handleGoToFeatureFlagAdmin = useCallback(() => {
    navigate(RoutesList.FeatureFlagAdminPage);
    onClose();
  }, [navigate, onClose]);

  const handleAreActionsPrivateToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!userInfo?.me?.id) {
        return;
      }

      updateUserInfo.mutate(
        {
          id: userInfo.me.id,
          soundsEnabled: userInfo?.me?.soundsEnabled ?? false,
          toThrive: userInfo?.me?.toThrive ?? '',
          language: userInfo?.me.language ?? 'en',
          isAllowedFeatureFlag: userInfo?.me?.isAllowedFeatureFlag ?? false,
          areActionsPrivate: !!event.target.checked,
        },
        {
          onSuccess: () => {
            //sync all events with cronofy
            cronofyBulkUpdateEventsPrivacy.mutate();
          },
        },
      );
    },
    [updateUserInfo, userInfo?.me?.id, userInfo?.me, cronofyBulkUpdateEventsPrivacy],
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right">
      <DrawerOverlay backgroundColor={`color-mix(in srgb, ${backgroundDefault} 80%, transparent)`} />
      <DrawerContent
        flexDirection="column"
        display="flex"
        boxShadow={`${rem(-6)} 0 ${rem(8)} 0 rgba(0, 0, 0, 0.10)`}
        whiteSpace="nowrap"
        backgroundColor="background-default"
      >
        <HStack
          alignItems="center"
          justifyContent="space-between"
          paddingTop={rem(28)}
          paddingBottom={rem(24)}
          color="text-primary"
          paddingX={rem(16)}
        >
          <Heading fontSize="2xl">{t('title')}</Heading>
          <IconButton
            aria-label={t('ariaLabels.close')}
            icon={<IconChevron />}
            onClick={onClose}
            size="xs"
            variant="secondary"
          />
        </HStack>
        <VStack alignItems="stretch" gap={0} overflow="hidden" overflowY="scroll" height="full">
          <VStack alignItems="stretch" paddingX={rem(16)} paddingY={rem(24)}>
            {userInfo?.me?.isAllowedFeatureFlag && (
              <>
                <Heading marginTop={rem(8)} fontSize="lg">
                  {t('sections.admin')}
                </Heading>
                <Button
                  justifyContent="flex-start"
                  leftIcon={<IconFeatureFlag />}
                  onClick={handleGoToFeatureFlagAdmin}
                  variant="tertiary"
                >
                  {t('labels.featureFlags')}
                </Button>
              </>
            )}
            <Heading fontSize="lg">{t('sections.audio')}</Heading>

            <HStack justifyContent="space-between" gap={rem(16)}>
              <Text>{t('labels.playSoundEffects')}</Text>
              <Switch isChecked={playSoundEffects} onChange={handleSoundEffectsToggle} />
            </HStack>

            <Heading fontSize="lg" marginY={rem(10)}>
              {t('sections.language')}
            </Heading>
            <LanguageSelector showLabel={false} />

            <Heading fontSize="lg">{t('sections.calendar')}</Heading>

            <HStack justifyContent="space-between" gap={rem(16)}>
              <Text>{t('labels.showDeclinedEvents')}</Text>
              <Switch isChecked={showDeclinedEvents} onChange={handleShowDeclinedEventsToggle} />
            </HStack>

            <HStack justifyContent="space-between" gap={rem(16)}>
              <Text>{t('labels.areActionsPrivate')}</Text>
              <Switch isChecked={areActionsPrivate} onChange={handleAreActionsPrivateToggle} />
            </HStack>
          </VStack>

          <Heading fontSize="lg" paddingX={rem(16)}>
            {t('sections.myCalendars')}
          </Heading>
          <CalendarsSettings separatorColor={separatorColor} />

          {/* RPM AI section — gated to @tonyrobbins.com accounts */}
          {import.meta.env.VITE_AGENT_ENABLED === 'true' && isCompanyUser && (
            <VStack alignItems="stretch" paddingX={rem(16)} paddingY={rem(16)}>
              <Heading fontSize="lg">RPM AI</Heading>
              <Button
                justifyContent="flex-start"
                leftIcon={<span>&#9992;&#65039;</span>}
                onClick={() => {
                  navigate(RoutesList.TelegramSettingsPage);
                  onClose();
                }}
                variant="tertiary"
              >
                Connect Telegram
              </Button>
              <Heading fontSize="lg" marginTop={rem(8)}>
                Connected Apps
              </Heading>
              <Text fontSize="xs" color="text-secondary">
                Connect your apps so RPM AI can manage your calendar, email, and more.
              </Text>
              <ConnectedAppsSettings />
              <Heading fontSize="lg" marginTop={rem(8)}>
                API Keys
              </Heading>
              <Text fontSize="xs" color="text-secondary">
                Create keys for external apps to access your RPM data.
              </Text>
              <ApiKeysSettings />
            </VStack>
          )}

          <Box
            paddingBottom={rem(16)}
            color="text-secondary"
            fontSize="md"
            fontWeight={500}
            lineHeight={rem(24)}
            paddingX={rem(16)}
            paddingY={rem(24)}
          >
            <Box as="dl">
              <Text as="dt">{t('labels.profileName')}</Text>
              <Text as="dd" color="cyan.400">
                {userInfo?.me?.firstName} {userInfo?.me?.lastName}
              </Text>
              <Text as="dt" paddingTop={rem(16)}>
                {t('labels.email')}
              </Text>
              <Text as="dd" color="cyan.400">
                {userInfo?.me?.email}
              </Text>
            </Box>

            <Button
              justifyContent="space-between"
              // Add a bit of padding to the button for better hover/focus
              // state, but keep children aligned with the parent border
              // Update: realised that focus state is disabled on tertiary variant
              // but kept this detail anyway because one day must be re-enabled
              width={`calc(100% + ${rem(16)})`}
              marginTop={rem(32)}
              marginLeft={rem(-8)}
              fontSize="inherit"
              fontWeight="inherit"
              onClick={handleLogout}
              paddingX={rem(8)}
              rightIcon={<IconLogout width={rem(20)} height={rem(20)} />}
              variant="tertiary"
            >
              {t('buttons.logout')}
            </Button>
          </Box>
          {showRpmHelpCenterButton && (
            <Button
              as={Link}
              flexShrink={0}
              marginBottom={rem(16)}
              href="https://docs.rpmplanner.com/"
              marginX={rem(16)}
              target="_blank"
              variant="secondary"
            >
              {t('buttons.helpCenterLinkText')}
            </Button>
          )}

          <Button
            as={Link}
            flexShrink={0}
            marginBottom={rem(16)}
            href="mailto:rpmfeedback@tonyrobbins.com"
            marginX={rem(16)}
            onClick={(e) => {
              e.preventDefault();
              handleEmailWithFallback('rpmfeedback@tonyrobbins.com', trackFeedbackSelected);
            }}
            target="_blank"
            variant="secondary"
          >
            {t('buttons.submitFeedback')}
          </Button>
        </VStack>

        <HStack
          textStyle="small"
          justifyContent="space-evenly"
          gap="none"
          marginTop="auto"
          marginRight={rem(chatbotWidth)}
          color="stroke-primary"
          borderTopWidth={rem(1)}
          borderTopStyle="solid"
          borderTopColor={separatorColor}
          paddingY={rem(16)}
        >
          <Link
            href="mailto:customerservice@tonyrobbins.com"
            onClick={(e) => {
              e.preventDefault();
              handleEmailWithFallback('customerservice@tonyrobbins.com');
            }}
            target="_blank"
          >
            {t('buttons.emailSupport')}
          </Link>
          <Link href="https://www.tonyrobbins.com/privacy-policy/" target="blank">
            {t('buttons.privacyPolicy')}
          </Link>
        </HStack>
      </DrawerContent>
    </Drawer>
  );
}

export default SettingsSidebar;
